import React, { ReactNode } from 'react';
import './header.scss';
import { isString } from 'lodash';
import { ReactComponent as BackIcon } from '@zonofi/svg-icons/arrow_left.svg';
import { ReactComponent as BackArrow } from '@zonofi/svg-icons/back-arrow.svg';
import { ReactComponent as HamburgerMenu } from '@zonofi/svg-icons/hamburger-menu.svg';
import {
  APP_TYPE,
  clearExtraDetails,
  clearSelectedItemIds,
  clearSelectedItemsMap,
  dispatch,
  removeSearchKey,
  Workspace,
} from '@zonofi/zono-money-store';
import { useNavigate } from 'react-router-dom';
import { SearchConfig } from '../../constants';
import { SearchBar } from '../../components';
import { TouchableOpacity } from '../../common';
import { NetworkStatus } from '../../network';

// ─── Sub-interfaces ────────────────────────────────────────────────────────────

export interface RightIconConfig {
  icon: string | JSX.Element;
  onPress?: () => void;
  allowCustomStyle?: boolean;
}

export interface LeftIconConfig {
  icon?: string | JSX.Element;
  onPress?: () => void;
}

export interface LayoutConfig {
  usingBaseLayout?: boolean;
  workspace?: Partial<Workspace>;
  appType?: APP_TYPE;
}

// ─── HeaderProps ───────────────────────────────────────────────────────────────

export interface HeaderProps {
  title?: string;
  /** Extra node rendered next to the title (field-ops uses this for a tag). */
  titleTag?: React.ReactNode;
  /** Rendered before the title text block (seller-hub uses this). */
  titlePrefix?: ReactNode;
  subtitle?: string;
  leftIcon?: LeftIconConfig;
  rightIcons?: RightIconConfig[];
  search?: SearchConfig;
  layout?: LayoutConfig;
  /**
   * When true the NetworkStatus widget is prepended to rightIcons.
   * Defaults to true for field-ops; false for seller-hub.
   */
  showNetworkStatus?: boolean;
}

// ─── Default left icon / press ────────────────────────────────────────────────

const DEFAULT_LEFT_ICON = <BackArrow color="#ffffff" />;

// ─── Component ────────────────────────────────────────────────────────────────

export const Header: React.FC<HeaderProps> = ({
  title = '',
  titleTag,
  titlePrefix,
  subtitle = '',
  leftIcon = {},
  rightIcons = [],
  search = {},
  layout = {},
  showNetworkStatus = false,
}) => {
  const navigate = useNavigate();

  const DEFAULT_LEFT_PRESS = () => {
    dispatch(clearSelectedItemIds());
    dispatch(clearSelectedItemsMap());
    dispatch(clearExtraDetails());
    dispatch(removeSearchKey());
    navigate(-1);
  };

  const {
    enabled: showSearch = false,
    showWindow: showSearchWindow = false,
    setShowWindow: setShowSearchWindow,
    placeholderText = 'Search',
    onSearchAction,
    customElement: searchElement,
    results: searchResults,
  } = search;

  const {
    icon: leftIconElement = DEFAULT_LEFT_ICON,
    onPress: onLeftPress = DEFAULT_LEFT_PRESS,
  } = leftIcon;

  const { usingBaseLayout = false, workspace: workspaceInfo } = layout;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const handleSearchEvent =
    (callback: () => void) => (e: React.SyntheticEvent) => {
      e.preventDefault();
      callback();
    };

  const handleEnterPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      // reserved for future use
    }
  };

  // ─── Left icon ────────────────────────────────────────────────────────────

  const renderLeftIcon = () => {
    if (usingBaseLayout && workspaceInfo)
      return <HamburgerMenu width={24} height={24} />;

    return isString(leftIconElement) ? (
      <img src={leftIconElement} alt="" />
    ) : (
      leftIconElement
    );
  };

  // ─── Right icons ──────────────────────────────────────────────────────────

  const renderRightIcon = (iconConfig: RightIconConfig, index: number) => (
    <TouchableOpacity
      className={
        iconConfig.allowCustomStyle ? '' : 'header-container--right_icons'
      }
      key={index}
      onPress={iconConfig.onPress}
    >
      {isString(iconConfig.icon) ? (
        <img
          src={iconConfig.icon}
          alt={`right-icon-${index}`}
          height={20}
          width={20}
        />
      ) : (
        iconConfig.icon
      )}
    </TouchableOpacity>
  );

  const allRightIcons: RightIconConfig[] = [
    ...(showNetworkStatus
      ? [{ icon: <NetworkStatus />, allowCustomStyle: true }]
      : []),
    ...rightIcons,
  ];

  // ─── Search ───────────────────────────────────────────────────────────────

  const renderSearchComponent = () => {
    if (searchElement) return searchElement;

    return (
      <SearchBar
        leftIcon={<BackIcon color="#000" />}
        placeholderText={placeholderText}
        onLeftClick={() => {
          dispatch(removeSearchKey());
          onSearchAction?.();
        }}
        handleFocus={handleSearchEvent(() => setShowSearchWindow?.(true))}
        handleClick={handleSearchEvent(() => setShowSearchWindow?.(true))}
        handleClear={handleSearchEvent(() => {
          dispatch(removeSearchKey());
          setShowSearchWindow?.(true);
        })}
        handleEnterPress={handleEnterPress}
      />
    );
  };

  // ─── Main content ─────────────────────────────────────────────────────────

  const renderHeaderContent = () => {
    if (showSearch) return renderSearchComponent();

    return (
      <>
        <div className="header-container-left">
          <TouchableOpacity onPress={onLeftPress}>
            {renderLeftIcon()}
          </TouchableOpacity>

          {titlePrefix}

          <div className="header-container-left-text">
            <span className="header-title font-semibold">
              {title}
              {titleTag && <>{titleTag}</>}
            </span>
            {subtitle && (
              <span className="header-subtitle font-regular">{subtitle}</span>
            )}
          </div>
        </div>

        {allRightIcons.length > 0 && (
          <div className="header-container--right">
            {allRightIcons.filter(Boolean).map(renderRightIcon)}
          </div>
        )}
      </>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="header">
      <div className="header-container">{renderHeaderContent()}</div>
      {showSearchWindow && searchResults}
    </div>
  );
};
